import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Layout } from 'syngenta-digital-cropwise-react-ui-kit';
import { Spinner } from '@agconnections/grow-ui';

import AddCircle from 'assets/add_circle.svg';
import CheckIcon from 'assets/check_circle_outline_outlined.svg';
import AlertIcon from 'assets/alertIcon.svg?react';
import RemoveCircle from 'assets/remove_circle.svg';
import SearchStateIcon from 'assets/search_state.svg';
import noSearchResultsFound from 'assets/no_search_results_found.svg';
import EmptyTableState from 'components/EmptyTableState';
import { ProductListContext } from 'screens/ProductList/context/ProductListProvider';
import filteredProducts, {
  getFilteredSearchProducts,
  getProductType,
  MINIMUM_SEARCH_LENGTH
} from 'screens/ProductList/helpers/filteredProducts';
import useProducts from 'hooks/useProducts';
import useDebounce from 'hooks/useDebounce';
import { INVENTORY_PRODUCT_SEARCH_PAGE_SIZE } from 'helpers/globalConstants';
import ProductSearchListRow from '../ProductSearchListRow';

const SearchSpinner = () => (
  <Col span={24} className="text-center">
    <div className="mt-56px">
      <Spinner size="lg" />
      <div className="text-base font-semibold mt-6 mb-8">Loading...</div>
    </div>
  </Col>
);

const renderSection = (title, products, isLoading, rowProps) => {
  if (isLoading) return <SearchSpinner />;
  if (!products.length) return null;
  return (
    <>
      <Layout.Header className="sticky top-0 z-10">
        <Row className="h-28px px-4 bg-blue-70 text-base text-white font-semibold items-center">
          {title}
        </Row>
      </Layout.Header>
      <Layout.Content>
        <div className="justify-content">
          {products.map(product => (
            <ProductSearchListRow
              key={product?.productApplicationId ?? product.id ?? product.name}
              product={product}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...rowProps(product)}
            />
          ))}
        </div>
      </Layout.Content>
    </>
  );
};

const MyProductsLoader = ({ reloadTrigger, onDataChange }) => {
  const { searchText } = useContext(ProductListContext);
  const {
    products: myProducts,
    isLoading: isLoadingMyProducts,
    reload: reloadMyProducts,
    filterByNameQuery
  } = useProducts({ size: INVENTORY_PRODUCT_SEARCH_PAGE_SIZE });

  const debouncedSearchText = useDebounce(searchText, 300);

  // Below the minimum length the backend must return the unfiltered list, so
  // the param is dropped (undefined is omitted from the request) instead of
  // being sent as an empty string.
  const searchBy =
    debouncedSearchText.length >= MINIMUM_SEARCH_LENGTH
      ? debouncedSearchText
      : undefined;

  useEffect(() => {
    filterByNameQuery(searchBy);
  }, [searchBy, filterByNameQuery]);

  useEffect(() => {
    onDataChange({ myProducts, isLoadingMyProducts });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myProducts, isLoadingMyProducts]);

  useEffect(() => {
    if (reloadTrigger > 0) reloadMyProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTrigger]);

  return null;
};

MyProductsLoader.propTypes = {
  reloadTrigger: PropTypes.number.isRequired,
  onDataChange: PropTypes.func.isRequired
};

const ProductSearchList = ({
  showProductList = false,
  setSelectedProductsList,
  selectedProductsList,
  handleOnEdit,
  typeFilters,
  listFilters = [],
  myProductsReloadTrigger = 0
}) => {
  const [allSearchProducts, setAllSearchProducts] = useState([]);
  const [myProducts, setMyProducts] = useState([]);
  const [isLoadingMyProducts, setIsLoadingMyProducts] = useState(false);
  const {
    productsInUse,
    searchedProducts,
    loadingProductSearch,
    isLoadingProductInUse,
    searchText
  } = useContext(ProductListContext);

  const applyTypeFilters = products => {
    if (!typeFilters?.length) return products;

    if (typeFilters.includes('CustomProduct')) {
      return products.filter(product => product.custom === true);
    }

    const normalizedTypeFilters = new Set(
      typeFilters.map(productType => productType?.toUpperCase())
    );

    return products.filter(product =>
      normalizedTypeFilters.has(getProductType(product)?.toUpperCase())
    );
  };

  let filteredMyProducts = [];
  if (showProductList) {
    if (searchText.length >= MINIMUM_SEARCH_LENGTH) {
      filteredMyProducts = filteredProducts(myProducts, searchText, []);
    } else {
      filteredMyProducts = myProducts;
    }
  }

  const displayedSelectedProductsList =
    searchText.length >= MINIMUM_SEARCH_LENGTH
      ? filteredProducts(selectedProductsList, searchText, [])
      : selectedProductsList;

  const typeFilteredSelectedProductsList = applyTypeFilters(
    displayedSelectedProductsList
  );

  const typeFilteredMyProducts = applyTypeFilters(filteredMyProducts);

  const showSelected = listFilters.length === 0;
  const showMyProductList =
    showProductList &&
    (listFilters.length === 0 || listFilters.includes('MyProductList'));
  const showAllProducts =
    listFilters.length === 0 || listFilters.includes('AllProducts');

  const handleOnRemove = prod => {
    setSelectedProductsList(
      (selectedProductsList || []).filter(selected => {
        // check with Romil, who worked on this Product List, a better way to handle this productApplicationId
        const hasAppId =
          'productApplicationId' in selected && 'productApplicationId' in prod;
        return hasAppId
          ? selected.productApplicationId !== prod.productApplicationId
          : selected.id !== prod.id;
      })
    );
  };

  const isEmptySearchList = () => {
    const hasSelected = showSelected && typeFilteredSelectedProductsList.length;
    const hasMyProducts = showMyProductList && typeFilteredMyProducts.length;
    const hasAllProducts = showAllProducts && allSearchProducts.length;
    const isLoading =
      loadingProductSearch || (showMyProductList && isLoadingMyProducts);
    return !hasSelected && !hasMyProducts && !hasAllProducts && !isLoading;
  };

  useEffect(() => {
    setAllSearchProducts(
      getFilteredSearchProducts({
        productsInUse,
        searchedProducts,
        typeFilters,
        searchText,
        myProducts,
        showProductList
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchedProducts,
    productsInUse,
    isLoadingProductInUse,
    searchText,
    selectedProductsList,
    loadingProductSearch,
    typeFilters,
    listFilters,
    myProducts,
    showProductList
  ]);

  const renderEmptyState = () => {
    if (!isEmptySearchList()) return null;

    const emptySearchStateMessage = showProductList
      ? 'Enter at least three characters to see a list of products from the Cropwise product list'
      : 'Enter at least three characters to see a list of products from the master list';

    return (
      <div className="w-full pt-62px">
        <EmptyTableState
          overlayIcon={
            searchText.length < MINIMUM_SEARCH_LENGTH
              ? SearchStateIcon
              : noSearchResultsFound
          }
          title={
            searchText.length < MINIMUM_SEARCH_LENGTH
              ? 'Search for Products'
              : 'No Products Found'
          }
          message={
            searchText.length < MINIMUM_SEARCH_LENGTH
              ? emptySearchStateMessage
              : 'Please try a different search or filter.'
          }
        />
      </div>
    );
  };

  const handleMyProductsData = ({
    myProducts: mp,
    isLoadingMyProducts: loading
  }) => {
    setMyProducts(mp);
    setIsLoadingMyProducts(loading);
  };

  return (
    <Layout>
      <Layout.Header className="sticky top-0 z-10">
        <Row className="h-44px bg-neutral-20 items-center">
          <Col className="w-120px" />
          <Col span={8} className="font-semibold">
            Product Name
          </Col>
          <Col span={4} className="font-semibold">
            Product Type
          </Col>
          <Col span={3} className="font-semibold">
            Standard Unit
          </Col>
          <Col flex="auto" className="font-semibold">
            EPA Number
          </Col>
        </Row>
      </Layout.Header>
      <Layout.Content className="flex-1 overflow-y-auto">
        {showProductList && (
          <MyProductsLoader
            reloadTrigger={myProductsReloadTrigger}
            onDataChange={handleMyProductsData}
          />
        )}
        {/* Selected Products Section */}
        {showSelected &&
          renderSection(
            `Selected (${typeFilteredSelectedProductsList.length})`,
            typeFilteredSelectedProductsList,
            false,
            product => ({
              primaryAction: () => handleOnRemove(product),
              secondaryAction: () => handleOnEdit(product),
              primaryIcon: RemoveCircle
            })
          )}
        {/* My Product List Section */}
        {showMyProductList &&
          renderSection(
            'My Product List',
            typeFilteredMyProducts,
            isLoadingMyProducts,
            product => ({
              primaryAction: () => handleOnEdit(product),
              secondaryAction: () => handleOnEdit(product),
              primaryIcon: AddCircle,
              secondaryIcon: CheckIcon,
              isPrimaryActionDisabled: product?.isInUse
            })
          )}
        {/* All Products Section */}
        {showAllProducts && (
          <>
            {(loadingProductSearch || allSearchProducts.length > 0) && (
              <Layout.Header className="sticky top-0 z-10">
                <Row className="h-28px px-4 bg-blue-70 text-base text-white font-semibold items-center">
                  All Products
                </Row>
              </Layout.Header>
            )}
            <Layout.Content>
              <Row className="items-center">
                {!loadingProductSearch && !isLoadingMyProducts ? (
                  <>
                    {allSearchProducts?.map(product => (
                      <ProductSearchListRow
                        key={product.id}
                        product={product}
                        primaryAction={() => handleOnEdit(product)}
                        secondaryAction={() => handleOnEdit(product)}
                        primaryIcon={AddCircle}
                        secondaryIcon={CheckIcon}
                        isPrimaryActionDisabled={product?.isInUse}
                      />
                    ))}
                    {allSearchProducts.length > 0 && (
                      <Row
                        className="w-full bg-blue-10 py-3 px-4"
                        style={{ borderTop: '1px solid #E0E2E8' }}
                      >
                        <AlertIcon className="mr-3" />
                        <div className="h-6 py-2px">
                          Displaying a selection of matching results. Add more
                          characters to your search to see additional matches.
                        </div>
                      </Row>
                    )}
                  </>
                ) : (
                  searchText.length >= MINIMUM_SEARCH_LENGTH && (
                    <SearchSpinner />
                  )
                )}
              </Row>
            </Layout.Content>
          </>
        )}
        {renderEmptyState()}
      </Layout.Content>
    </Layout>
  );
};

ProductSearchList.defaultProps = {
  showProductList: false,
  listFilters: [],
  myProductsReloadTrigger: 0
};

ProductSearchList.propTypes = {
  setSelectedProductsList: PropTypes.func.isRequired,
  typeFilters: PropTypes.arrayOf(PropTypes.object).isRequired,
  listFilters: PropTypes.arrayOf(PropTypes.string),
  selectedProductsList: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      manufacturer: PropTypes.string,
      producttype: PropTypes.string,
      isInUse: PropTypes.bool
    })
  ).isRequired,
  handleOnEdit: PropTypes.func.isRequired,
  showProductList: PropTypes.bool,
  myProductsReloadTrigger: PropTypes.number
};

export default ProductSearchList;
